import Config from 'config';
import { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import propsStore from '../../../../../store/propsStore/propsStore';
import validationsStore from '../../../../../store/validation/validationStore';
import { TApiaFieldPropsObj, TFileUploaded } from '../../../../../types';
import { downloadUrl } from '../../../../../utils/util';
import {
  TSaveDroppedFilesConf,
  TUploaderFileInfo,
} from '../../../../common/uploader/types';
import UploaderApi, {
  isOneClickUploadEnabled,
  parseFileDefinition,
  TOnUploadProgress,
} from '../../../../common/uploader/UploaderApi';
import fieldsStore from '../../fieldsStore/fieldsStore';

export function parseFakeJSON(fakeJSON: string) {
  return [...fakeJSON.matchAll(/(\w+): '?([^']+)'?/g)]
    .map(([, k, v]) => ({
      id: k,
      label: v,
    }))
    .reduce<Record<number, string>>((returnObject, current) => {
      // eslint-disable-next-line no-param-reassign
      returnObject[Number(current.id)] = current.label;
      return returnObject;
    }, {});
}

export default function makeUploaderApi({
  langs,
  fieldId,
  initialFiles,
  fieldProps,
  translationId,
}: {
  langs?: string;
  fieldId: string;
  initialFiles: Record<string, TFileUploaded>;
  fieldProps: TApiaFieldPropsObj;
  translationId?: string;
}) {
  const props = () => propsStore.getFieldProps(fieldId);
  const index = () => {
    return (
      Config.MODAL_INDEX ?? props()?.field?.multIdx ?? props()?.attributes.index
    );
  };
  const elemId = `dropUpprDiv${fieldId}_${index()}${
    window.IS_EDIT_GRID_MODAL ? '_true_Mdl' : ''
  }`;
  const isEditionMode = window.IS_EDIT_GRID_MODAL ? true : undefined;

  return class ApiaUploaderApi extends UploaderApi {
    constructor(type: 'E' | 'P') {
      super(type, fieldId, false);
      this.allowTranslations = !!langs;

      this.langs = parseFakeJSON(langs || '');

      this.configurationObject = {
        allowDocumentsMonitor: !fieldProps.fileNotShowDocMon,
        allowPickFromDirectories: fieldProps.fileShwFoldTreeBtn !== undefined,
        collapseDirectories: !!fieldProps.fileCollapseFldStrc,
        collapseMetadata: !!fieldProps.fileCollapseMetadata,
        collapsePermissions: !!fieldProps.fileCollapsePermission,
        defaultDirectory: fieldProps.fileDefFolder,
        monitorId: fieldProps.documentMonitorCus,
        oneClickUpload: isOneClickUploadEnabled(
          translationId !== '' ? true : !!fieldProps.oneClickUpload,
        ),
        showDirectoriesStructure: fieldProps.fileShwFoldTreeStr === false,
        showExpDate: fieldProps.fileExpDate === true,
        showMetadata: !fieldProps.hideDocMetadata,
        showPermissions: !fieldProps.hideDocPermissions,
        translationId,
      };
      this.maxFiles = 1;
    }

    protected refreshShownFiles(shouldNotify = false) {
      const validationsState = this.getValidationStore();
      const newShownFiles = this.filterAcceptedFiles(
        this.getStore().inProgressFiles,
        shouldNotify,
        validationsState.cmbDocType,
      );
      if (typeof newShownFiles !== 'string')
        this.updateModal({
          shownFiles: newShownFiles.slice(-1),
        });
    }

    protected filterByFilesAmmountLimit(files: File[]): File[] {
      const { inProgressFiles } = this.getStore();
      if (inProgressFiles.length === 1 && files.length)
        if (inProgressFiles[0].name !== files[0].name) {
          this.updateStore({ inProgressFiles: [] });
        } else return [];
      return files.slice(0, 1);
    }

    evaluateDeleteDocumentResult(
      result: undefined | { success: boolean },
    ): boolean {
      return !!result?.success;
    }

    getAjaxUrl(): string {
      return '/apia.execution.FormAction.run';
    }

    getAllowedTypesData() {
      return {
        postData: {
          docTypeId: props().field?.properties.docType,
          forDropElem: true,
        },
        queryData: {
          elemType: elemId,
          frmId: props().formData?.frmId,
          frmParent: props().formData?.frmParent,
          attId: props().field?.attId,
          fromFormElem: true,
          docTypeId: props().field?.properties.docType,
          index: index(),
          frmOut: true,
          editionMode: isEditionMode,
        },
      };
    }

    getAjaxUploadFileStatusParameters = (
      defaultParameters: Record<string, unknown>,
    ): Record<string, unknown> => {
      return {
        ...defaultParameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        forceDocTypeId: props().field?.properties.docType,
        prefix: props().formData?.frmParent,
        editionMode: isEditionMode,
      };
    };

    getAjaxUploadStartParameters = ({
      useDocTypePermitted,
      docTypePermittedObjType,
      docTypePermittedObjId,
      ...defaultParameters
    }: Record<string, unknown>): Record<string, unknown> => {
      return {
        ...defaultParameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        forceDocTypeId: props().field?.properties.docType,
        prefix: props().formData?.frmParent,
        editionMode: isEditionMode,
      };
    };

    getCheckLockDocumentParameters = ({
      prefix,
      ...parameters
    }: Record<string, unknown>) => {
      return {
        ...parameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        editionMode: isEditionMode,
      };
    };

    getCheckSignatureParameters = (parameters: Record<string, unknown>) => {
      return {
        ...parameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        prefix: parameters.prefix ?? props().formData?.frmParent ?? this.type,
      };
    };

    getClearTempFilesParameters = ({
      elemType,
      ...rest
    }: Record<string, unknown>) => {
      return {
        ...rest,
        frmParent: props().formData?.frmParent,
        frmId: props().formData?.frmId,
        attId: props().field?.attId,
        index: index(),
      };
    };

    getConfirmDropModalParameters = (
      defaultParameters: Record<string, unknown>,
    ): Record<string, unknown> => {
      return {
        ...defaultParameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        elemType: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        forceDocTypeId: props().field?.properties.docType,
        editionMode: isEditionMode,
      };
    };

    getConfirmDropModalPostdata = ({
      pe,
      ...parameters
    }: Record<string, unknown>): Record<string, unknown> => {
      return {
        ...parameters,
        editionMode: isEditionMode,
      };
    };

    getDeleteDocumentParameters = ({
      prefix,
      isAjax,
      ...currentParameters
    }: Record<string, unknown>) => {
      return {
        ...currentParameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        editionMode: isEditionMode,
      };
    };

    getDocumentInfoParameters = (parameters: Record<string, unknown>) => {
      return { ...parameters, ajaxUrl: window.URL_REQUEST_AJAX };
    };

    getEditDocumentParameters = ({
      action,
      docId,
    }: Record<string, unknown>) => {
      return {
        action,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        docId,
        ajaxUrl: 'apia.execution.FormAction.run',
      };
    };

    getLoadFilesForFolderParameters: () => Record<string, unknown> = () => {
      return {
        useDocTypePermitted:
          this.getStore().allowedTypes.length === 1 ? true : undefined,
        docTypeForcedId:
          this.getStore().allowedTypes.length === 1
            ? this.getStore().allowedTypes[0].id
            : undefined,
        txtCantDoc: 0,
        loadFolders: true,
      };
    };

    getLoadFileSystemStructureTree = () => ({
      useDocTypePermitted: this.getStore().allowedTypes.length === 1,
      docTypeForcedId: this.docTypePermittedObjId,
    });

    getLockDocumentParameters = ({
      prefix,
      isAjax,
      ...currentParameters
    }: Record<string, unknown>) => {
      return {
        ...currentParameters,
        frmId: props().formData?.frmId,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        editionMode: isEditionMode,
      };
    };

    getMarkFileToSignParameters = ({
      docId,
      prefix,
      ...defaultParameters
    }: Record<string, unknown>) => {
      return {
        ...defaultParameters,
        action: 'sign',
        frmParent: props().formData?.frmParent,
        frmId: props().formData?.frmId,
        fldId: props().field?.id,
        attId: props().field?.attId,
        index: index(),
        editionMode: isEditionMode,
      };
    };

    getProcessDroppedFilesParameters = (
      defaultParameters: Record<string, unknown>,
    ): Record<string, unknown> => {
      return {
        ...defaultParameters,
        elemType: elemId,
        frmId: props().formData?.frmId,
        fromFormElem: true,
        frmParent: props().formData?.frmParent,
        attId: props().field?.attId,
        index: index(),
        editionMode: isEditionMode,
      };
    };

    getProcessDroppedFilesPostdata = ({
      useDocTypePermitted,
      docTypePermittedObjId,
      docTypePermittedObjType,
      ...parameters
    }: Record<string, unknown>): Record<string, unknown> => {
      return { ...parameters, editionMode: isEditionMode };
    };

    getReloadMetadataParameters = (
      defaultParameters: Record<string, unknown>,
    ): Record<string, unknown> => {
      return {
        ...defaultParameters,
        ...(window.IS_EDIT_GRID ? { editionMode: true } : null),
        frmParent: props().formData?.frmParent,
        frmId: props().formData?.frmId,
        attId: props().field?.attId,
        index: index(),
        prefix: props().formData?.frmParent,
      };
    };

    getSaveDroppedFilesParameters = (
      defaultParameters: Record<string, unknown>,
    ): Record<string, unknown> => {
      return {
        ...defaultParameters,
        frmParent: props().formData?.frmParent,
        frmId: props().formData?.frmId,
        attId: props().field?.attId,
        index: index(),
        fromFormElem: true,
        elemType: elemId,
        elemId,
        editionMode: isEditionMode,
      };
    };

    async downloadDocument(fileId: string): Promise<void> {
      const file = this.indexedFiles[fileId];

      if (window.IN_MONITOR || (await this.checkWebDavLock(file.docId)))
        await downloadUrl(
          makeApiaUrl({
            action: 'download',
            frmParent: props().formData?.frmParent,
            frmId: props().formData?.frmId,
            attId: props().field?.attId,
            index: index(),
            docId: file.downloadDocId,
            ajaxUrl: '/page/generic/downloadAttDoc.jsp',
            editionMode: isEditionMode,
          }),
        );
    }

    loadCurrentDocuments(): Promise<void> {
      return new Promise((resolve) => {
        const isReadonly =
          [
            props().readOnly,
            props().readonly,
            props().formData?.readOnly,
          ].findIndex((el) => String(el) === 'true') !== -1;
        this.currentConfiguration = {
          allowLock: !isReadonly,
          allowMultiple: false,
          allowSign: !isReadonly,
          readOnly: isReadonly,
        };
        const originalFile = initialFiles[0];
        if (originalFile) {
          this.filesActions.add(
            parseFileDefinition(originalFile, fieldProps.reqSign || false),
          );
          Object.entries(initialFiles).forEach(([langId, definition]) => {
            if (Number(langId) !== 0)
              this.setTranslationFile(
                parseFileDefinition(definition, fieldProps.reqSign || false),
                originalFile.docId,
                Number(langId),
              );
          });
        }
        resolve();
      });
    }

    public parseFileDefinition = (file: TFileUploaded): TUploaderFileInfo => {
      return {
        ...parseFileDefinition(file, fieldProps.reqSign || false),
        canEdit: file.canEdit ?? true,
        canRead: file.canRead ?? true,
      };
    };

    async saveDroppedFiles(
      unprocessedFiles: File[],
      onUploadProgress: TOnUploadProgress,
      conf?: TSaveDroppedFilesConf | undefined,
    ): Promise<void> {
      const result = await super.saveDroppedFiles(
        unprocessedFiles,
        onUploadProgress,
        conf,
      );

      fieldsStore.getById(this.id).setError(null);

      return result;
    }

    async version(
      file: TUploaderFileInfo,
      onUploadProgress: TOnUploadProgress,
      conf: {
        newFiles?: File[];
        langId?: number;
        translatingFile?: TUploaderFileInfo;
      } = { newFiles: [] },
    ) {
      if (
        (await this.checkLockDocument(file.docId, true)) === true &&
        (await this.checkWebDavLock(file.docId))
      ) {
        this.resetModal();
        validationsStore.setFieldValue(
          this.modalStoreId,
          'versioningFile',
          file,
        );
        if (file.docTypeId)
          this.updateModal({
            allowChangeDocType: false,
            cmbDocType: file.docTypeId,
          });
        await this.versionFileInfo(file);
        if (conf.newFiles && conf.newFiles.length > 0) {
          await this.ajaxUploadStart();
          await this.saveDroppedFiles(conf.newFiles, onUploadProgress, {
            strictMode: file.docTypeId !== undefined && file.docTypeId !== '',
            langId: conf.langId,
            translatingFile: conf.translatingFile,
            shouldReset: false,
          });
        } else
          this.openModal({
            langId: conf.langId,
            translatingFile: conf.translatingFile,
            shouldReset: false,
          });
      }
    }
  };
}
